import styled from '@emotion/styled';
import parse, { domToReact, Element } from 'html-react-parser';
import { forwardRef, useMemo } from 'react';
import { KahlerBrushLineThrough } from '~/features/brand-effects/components/KahlerBrush/KahlerBrushLineThrough';
import { IHeadline, IHeadlineOptionalRichText, IHeadlineRichText } from '~/lib/data-contract';
import { DIGIT_REGEX } from '~/shared/components/RawHtml/hooks/useRawHtml';
import { Text, TextVariants } from '~/shared/components/Text/Text';
import { useModule } from '~/templates/blocks/components/ModuleContainer/hooks/useModule';

export const StyledModuleHeaderLabel = styled(Text)({
	position: 'relative',
	zIndex: 2,
});

type ModuleHeaderProps = {
	headline?: IHeadlineOptionalRichText;
	label?: IHeadline;
	id?: string;
	className?: string;
};

const isHtmlHeadline = (headline: IHeadlineOptionalRichText): headline is IHeadlineRichText => {
	return typeof headline.html !== 'undefined';
};

const hToDisplayConverter = (variant?: string) => {
	if (!variant) {
		return 'display3';
	}

	const size = Number(variant.replace(DIGIT_REGEX, ''));
	return `display${size}` as TextVariants;
};

type ModuleHeaderElement = HTMLHeadingElement | HTMLParagraphElement;
type ModuleHeaderHeadlineProps = Pick<ModuleHeaderProps, 'headline' | 'id' | 'className'>;
type ModuleHeaderParsedHeadline = Exclude<ModuleHeaderHeadlineProps, 'headline'> & {
	headline: IHeadlineRichText;
};

const ModuleHeaderParsedHeadline = forwardRef<ModuleHeaderElement, ModuleHeaderParsedHeadline>(({ headline, id, className }, ref) => {
	const { backgroundColor, activeBrandEffect } = useModule();

	const markup = useMemo(() => {
		return parse(headline.html, {
			replace: (domNode) => {
				const { attribs, children } = domNode as Element;

				if (attribs?.style === 'text-decoration: underline;') {
					return activeBrandEffect === 'kah' ? (
						<KahlerBrushLineThrough
							children={domToReact(children)}
							colorToComplement={backgroundColor}
							randomizePosition={{ x: 0.1, y: 0.4 }}
						/>
					) : (
						<span children={domToReact(children)}></span>
					);
				}

				return;
			},
		});
	}, [headline.html]);

	const variant = useMemo(() => {
		return hToDisplayConverter(headline.style);
	}, [headline.style]);

	return (
		<Text
			id={id}
			className={className}
			ref={ref}
			as={headline.tag}
			variant={variant}
			children={markup}
			css={{ isolation: 'isolate' }} // Elevate z-index to be able to draw Kähler brush strokes beneath text
		/>
	);
});

const ModuleHeaderHeadline = forwardRef<ModuleHeaderElement, ModuleHeaderHeadlineProps>(({ headline, id, className }, ref) => {
	if (!headline) {
		return null;
	}

	if (isHtmlHeadline(headline)) {
		return (
			<ModuleHeaderParsedHeadline
				headline={headline}
				id={id}
				className={className}
				ref={ref}
			/>
		);
	}

	const variant = useMemo(() => {
		return hToDisplayConverter(headline.style);
	}, [headline.style]);

	return (
		<Text
			id={id}
			className={className}
			ref={ref}
			as={headline.tag}
			variant={variant}
			children={headline.text}
		/>
	);
});

export const ModuleHeader = forwardRef<ModuleHeaderElement, ModuleHeaderProps>(({ headline, label, id, className }, ref) => {
	return headline ? (
		<>
			{label ? (
				<StyledModuleHeaderLabel
					as={label.tag}
					variant={'label'}
					children={label.text}
				/>
			) : null}
			<ModuleHeaderHeadline
				ref={ref}
				headline={headline}
				id={id}
				className={className}
			/>
		</>
	) : null;
});
